/**
 * KetoFusionFinale — ceremonial conclusion screen for the 12-week program.
 * Route: /keto-fusion/finale
 *
 * Shown when enrollment.status === "completed". Displays:
 *   - Hero with user's name + duration + 84/84 missions
 *   - 4 phases summary with KPIs
 *   - Milestones timeline (all reached)
 *   - Identity statement (read-only if saved)
 *   - Shareable transformation card (PNG via html2canvas-style — text only)
 *   - "Continue forever" CTA → returns to hub
 */

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Trophy, Sparkles, Check, Flame, Atom, Dumbbell, Brain, Leaf, Target, Zap, ArrowRight, Share2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const ICONS = { Trophy, Sparkles, Flame, Atom, Dumbbell, Brain, Leaf, Target, Zap };

export default function KetoFusionFinale() {
  const navigate = useNavigate();
  const [finale, setFinale] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/keto-fusion/finale`, { headers: authHeaders() });
        const data = await res.json();
        setFinale(data);
      } catch {
        toast.error("Erro ao carregar a cerimónia");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}>
          <Trophy className="w-10 h-10 text-emerald-400" />
        </motion.div>
      </div>
    );
  }

  if (!finale?.ready) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6 text-center">
        <div className="max-w-md">
          <Trophy className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-zinc-100 mb-2">A cerimónia ainda não está pronta</h2>
          <p className="text-sm text-zinc-400 mb-6">
            {finale?.message || "Completa as 84 missões para desbloquear esta página."}
            {finale?.completion_pct != null && (
              <span className="block mt-2 text-emerald-400 font-semibold">{finale.completion_pct}% completo</span>
            )}
          </p>
          <Button
            onClick={() => navigate("/keto-fusion/today")}
            className="bg-gradient-to-r from-lime-500 to-purple-500 text-white"
            data-testid="finale-back-dashboard"
          >
            Voltar ao Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    const text = `Conclui o Keto Fusion — 12 semanas, ${finale.missions_completed} missões. ${finale.identity_statement ? "\n\n\"" + finale.identity_statement + "\"" : ""}\n\n#KetoLabMethod #KetoFusion`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Keto Fusion — Concluído", text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Copiado para a área de transferência ✨");
      }
    } catch {
      toast.info("Partilha cancelada");
    }
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100 relative overflow-x-hidden" data-testid="fusion-finale-page">
      {/* Confetti-like ambient pulses */}
      <div className="fixed inset-0 pointer-events-none">
        <motion.div
          className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full blur-[140px]"
          style={{ background: "radial-gradient(circle, rgba(0,255,136,0.25), transparent)" }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full blur-[140px]"
          style={{ background: "radial-gradient(circle, rgba(168,85,247,0.25), transparent)" }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 7, repeat: Infinity, delay: 1.5 }}
        />
      </div>

      {/* Floating particles */}
      {Array.from({ length: 24 }).map((_, i) => (
        <motion.div
          key={i}
          className="fixed w-1.5 h-1.5 rounded-full pointer-events-none"
          style={{
            left: `${(i * 37) % 100}%`,
            bottom: "-2%",
            background: i % 2 === 0 ? "#00ff88" : "#a855f7",
            boxShadow: `0 0 10px ${i % 2 === 0 ? "#00ff88" : "#a855f7"}`,
          }}
          animate={{ y: [0, -700], opacity: [0, 1, 0] }}
          transition={{ duration: 6 + (i % 4), repeat: Infinity, delay: i * 0.4 }}
        />
      ))}

      <div className="max-w-4xl mx-auto px-5 py-10 relative">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          className="text-center mb-12"
          data-testid="finale-hero"
        >
          <motion.div
            animate={{ rotate: [0, 5, -5, 0], scale: [1, 1.08, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="inline-block mb-5"
          >
            <Trophy className="w-20 h-20 mx-auto text-emerald-400" style={{ filter: "drop-shadow(0 0 30px rgba(0,255,136,0.7))" }} />
          </motion.div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900 border border-emerald-500/30 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-[10px] uppercase tracking-widest text-emerald-300">Cerimónia de Conclusão</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-3" style={{
            background: "linear-gradient(135deg, #84cc16, #00ff88, #a855f7)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textShadow: "0 0 40px rgba(0,255,136,0.3)",
          }}>
            Concluíste.
          </h1>
          <p className="text-lg text-zinc-200 mb-1">
            {finale.user_name ? <><span className="font-semibold text-emerald-300">{finale.user_name}</span>, </> : ""}
            atingiste o topo do programa.
          </p>
          <p className="text-sm text-zinc-400">
            <span className="text-emerald-400 font-bold">{finale.missions_completed}/{finale.missions_total}</span> missões ·{" "}
            {finale.days_total ? <><span className="text-emerald-400 font-bold">{finale.days_total}</span> dias · </> : ""}
            <span className="text-emerald-400 font-bold">12</span> semanas
          </p>
        </motion.div>

        {/* Phases summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-10"
        >
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3 text-center">A tua jornada em 4 fases</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="finale-phases">
            {finale.phases_summary?.map((p) => (
              <motion.div
                key={p.block}
                whileHover={{ y: -4 }}
                className="p-4 rounded-2xl border bg-zinc-950"
                style={{ borderColor: `${p.color}40`, boxShadow: `0 8px 24px ${p.color}15` }}
              >
                <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: p.color }}>
                  Bloco {p.block}
                </p>
                <h3 className="text-sm font-bold text-zinc-100 mb-1">{p.name_pt}</h3>
                <p className="text-[11px] italic text-zinc-400 mb-3 leading-snug">{p.tagline_pt}</p>
                <div className="space-y-1">
                  {p.kpis?.slice(0, 2).map((k, i) => (
                    <div key={i} className="flex justify-between text-[11px]">
                      <span className="text-zinc-500">{k.label_pt}</span>
                      <span className="font-semibold" style={{ color: p.color }}>{k.target_pt}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Milestones timeline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-10"
        >
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-4 text-center">Milestones alcançados</p>
          <div className="space-y-2" data-testid="finale-milestones">
            {finale.milestones?.filter((m) => m.reached).map((m, i) => {
              const Icon = ICONS[m.icon] || Sparkles;
              return (
                <motion.div
                  key={m.code}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.7 + i * 0.08 }}
                  className="flex items-center gap-3 p-3 rounded-xl bg-zinc-950 border border-zinc-800"
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0"
                    style={{ borderColor: m.color, background: `${m.color}15`, boxShadow: `0 0 15px ${m.color}50` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: m.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-zinc-100">{m.title_pt}</h4>
                    <p className="text-[11px] text-zinc-400 leading-snug">{m.body_pt}</p>
                  </div>
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Identity statement */}
        {finale.identity_statement && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="mb-10 p-6 rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-purple-500/5"
            data-testid="finale-identity"
          >
            <p className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">
              <Brain className="w-3 h-3 inline mr-1" /> O teu Identity Statement
            </p>
            <blockquote className="text-base sm:text-lg italic text-zinc-100 leading-relaxed border-l-2 border-emerald-400 pl-4">
              &ldquo;{finale.identity_statement}&rdquo;
            </blockquote>
          </motion.div>
        )}

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="flex flex-wrap gap-3 justify-center"
        >
          <Button
            onClick={handleShare}
            className="bg-zinc-900 border border-zinc-700 text-zinc-100 hover:bg-zinc-800 h-12 px-6"
            data-testid="finale-share-btn"
          >
            <Share2 className="w-4 h-4 mr-2" /> Partilhar conquista
          </Button>
          <Button
            onClick={() => navigate("/")}
            className="bg-gradient-to-r from-lime-500 via-emerald-500 to-purple-500 hover:opacity-90 text-white font-bold h-12 px-8 shadow-lg shadow-emerald-500/40"
            data-testid="finale-continue-btn"
          >
            Continuar a viver fusion <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    </div>
  );
}
